// The onSubmit handler can return either void or Promise<void | T>, which triggers
// the no-misused-promises rule when passed to form.handleSubmit. This is a false positive
// since react-hook-form properly handles both synchronous and asynchronous submit handlers.
/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { FormProps } from '../../../types/fileExplorerTypes';
import { UploadFileSchema } from './schemas';
import { InputField, RadioField } from '../../Forms/Fields';
import { DropzoneField } from '../../Forms/Fields/DropzoneField';
import { formsConfig } from './config';
import { ValidatedFile, validateFiles } from '../services';
import { FileIcon } from '../FileExplorerIcons';
import { DuplicateActionOptions } from '../constants';
import { Alert } from '../../common/Alert';

function DuplicatedFilesList({ files }: { files: ValidatedFile[] }) {
  return (
    <div>
      <p className="fs-sm">Esta configuración afecta a los archivos:</p>
      <ul className="list-unstyled d-flex flex-row gap-2 flex-wrap">
        {files.map((file) => (
          <li key={file.original_filename} className="p-2 rounded-2 border border-gray-300">
            <div className="d-flex align-items-center gap-2">
              <FileIcon height={16} width={16} />
              <span className=" fs-xs">
                {file.formatted_title}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DuplicatesFormSkeleton() {
  return (
    <div className="placeholder-glow d-flex flex-column gap-3">
      <div className="d-flex flex-column gap-2">
        <div className="placeholder col-12 rounded-1" />
        <div className="placeholder col-3 rounded-1" />
      </div>

      <div className="d-flex flex-row gap-4">
        <div className="placeholder w-100 rounded-1" />
        <div className="placeholder w-100 rounded-1" />
      </div>
      <div className="placeholder col-4 rounded-1" />

    </div>
  );
}

type UploadFileFormProps = Omit<FormProps<UploadFileSchema>, 'children'> & {
  children: React.ReactNode | ((props: { isValidating: boolean }) => React.ReactNode);
};

export function UploadFileForm({ form, children, onSubmit }: UploadFileFormProps) {
  const { watch } = form;
  const files = watch('files');
  const category = watch('category');
  const isMultipleFiles = files?.length > 1;
  const showFileMetaFields = !isMultipleFiles && files.length > 0;

  const validateFilesMutation = useMutation({
    mutationFn: (
      params: {
        filenames: string[];
        categoryId: number;
      }
    ) => validateFiles(params.filenames, params.categoryId),
    onSuccess: (data) => {
      data.validated_files.forEach((validatedFile, index) => {
        form.setValue(`filesMeta.${index}.title`, validatedFile.formatted_title);
      });

      if (data.duplicates.length === 1 && files?.length === 1) {
        const duplicate = data.duplicates[0];
        if (duplicate.existing_file) {
          form.setValue('filesMeta.0.description', duplicate.existing_file.description);
        }
      }
    },
  });

  useEffect(() => {
    if (!files || files.length === 0 || !category) {
      return;
    }

    const filenames = files.map((file) => file.name);
    validateFilesMutation.mutate({ filenames, categoryId: category });
  }, [files, category]);

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="modal-form">
        <DropzoneField<UploadFileSchema>
          name="files"
          label="Archivo"
          accept={formsConfig.commonAcceptedFiles}
          multiple
          showPreview
        />
        {showFileMetaFields && (
          <>
            <InputField<UploadFileSchema>
              id="title"
              path="filesMeta.0.title"
              label="Nombre"
              placeholder="Nombre del archivo"
              required
            />
            <InputField<UploadFileSchema>
              id="description"
              path="filesMeta.0.description"
              label="Descripción"
              placeholder="Descripción del archivo"
            />
          </>
        )}

        {validateFilesMutation.isLoading && <DuplicatesFormSkeleton />}
        {validateFilesMutation.isError && (
        <Alert variant="danger">
          Hubo un error al validar los archivos que subiste.
          Por favor, recarga la página e intenta nuevamente.
        </Alert>
        )}
        {validateFilesMutation.data?.has_duplicates && (
        <>
          <RadioField<UploadFileSchema>
            path="duplicateAction"
            label="¿Qué hacer cuando el nombre del archivo coincida con el nombre de un archivo existente?"
            options={DuplicateActionOptions.asRadioOptions()}
          />
          <DuplicatedFilesList files={validateFilesMutation.data.duplicates} />
        </>
        )}

        {typeof children === 'function' ? children({ isValidating: validateFilesMutation.isLoading }) : children}
      </form>
    </FormProvider>
  );
}
